// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import OEQuantityTemplate from '@front/templates/OEQuantityTemplate.html';
import { ContainerControl } from '@front/controls/elements/ContainerControl';
import { Resources } from '@shared/localizations/Resources';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { ControlsTypes } from '@front/controls/UtilsClasses/FactoryConstants';
import { MarginInfoParameters } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoParameters';
import { MarginInfoWrapper } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoWrapper';
import { OrderEditBaseUtils } from '@shared/utils/Trading/OrderEditBaseUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class OEQuantity extends ContainerControl {
    constructor() {
        super();
        this.cashMarginRequestTimeoutID = null; // If Margin info is opened Quantity should be updated together with other margin parameters in one request (MarginOEControl->RefreshByMarginRequest)
        this.cashMarginRequestTimeoutDuration = 200; // timeout delay duration (in milliseconds) before sending the margin request
        this.autoMarginRequestIntervalID = null; // If Margin info isn’t opened, then for Market order CHECK_MARGIN_REQ should be sent automatically every 10 seconds.
        this.autoMarginRequestIntervalDuration = 10000; // interval duration (in milliseconds)
        this.lastLimitPrice = null;
        this.lastStopPrice = null;
        this.lastTrailingStopOffsetValue = null;
    }
    getType() {
        return ControlsTypes.OEQuantity;
    }
    oninit() {
        super.oninit();
        this.observe('value', this.onValueChanged);
        this.observe('inLots', this.updateQuantity);
        this.observe('instrument account productType orderType showLabel', this.updateIsCashAvailable, { init: false });
        this.observe('isCashAvailable', this.onIsCashAvailableChanged, { init: false });
        this.observe('qtyCashSelectedItem', this.onQtyCashSelectedItemChanged);
        this.observe('estQtyText estQtyValue inLots isCashMode', this.updateEstQuantity);
        this.observe('account isCashMode', this.updateAssetName);
        this.observe('orderEditParameterArray', this.onPriceCouldChanged);
        this.observe('cash operation', this.handleCashMarginRequest); // may need send margin request to get Est.qty value if MarginOE is absent or closed
        this.observe('isCashMode', this.onIsCashModeChanged, { init: false }); // subscribe to outside changing (e.g. callBack of OE)
        this.observe('skipMarginRequest orderType isCashMode', this.observeIfMarginRequestNeeded);
        this.localize();
    }
    oncomplete() {
        super.oncomplete();
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        this.updateIsCashAvailable();
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        if (this.cashMarginRequestTimeoutID) {
            clearTimeout(this.cashMarginRequestTimeoutID);
        }
        if (this.autoMarginRequestIntervalID) {
            clearInterval(this.autoMarginRequestIntervalID);
        }
        super.dispose();
    }
    setFocus() {
        super.setFocus();
        this.Controls.QN.setFocus();
    }
    onValueChanged(value) {
        const isCashMode = this.get('isCashMode');
        if (isCashMode) {
            void this.set('cash', value);
        }
        else {
            this.updateQuantity();
        }
    }
    updateQuantity() {
        const isCashMode = this.get('isCashMode');
        if (!isCashMode) {
            const value = this.get('value');
            const inLots = this.get('inLots');
            void this.set('quantity', new Quantity(value, inLots));
        }
    }
    localize() {
        this.localizeEstQtyLabel();
        this.localizeQtyCashLabel();
    }
    localizeEstQtyLabel() {
        void this.set('estQtyText', Resources.getResource('EstQuantity'));
    }
    localizeQtyCashLabel() {
        const item = this.get('qtyCashSelectedItem');
        const localKey = item === null || item === void 0 ? void 0 : item.text;
        void this.set('label', Resources.getResource(localKey !== null && localKey !== void 0 ? localKey : 'panel.newOrderEntry.amountLabel'));
    }
    updateCashSelection(isCashAvailableArg) {
        const qtyCashItems = this.get('qtyCashItems');
        const isCashAvailable = isCashAvailableArg !== null && isCashAvailableArg !== void 0 ? isCashAvailableArg : this.get('isCashAvailable');
        let qtyCashSelectedItem = this.get('qtyCashSelectedItem');
        const cashForbidden = !isCashAvailable;
        if (isNullOrUndefined(qtyCashSelectedItem) || (cashForbidden && this.isCashContextItem(qtyCashSelectedItem))) {
            qtyCashSelectedItem =
                isCashAvailable && SessionSettings.IsCashModePreferred() ? qtyCashItems[1] : qtyCashItems[0];
        }
        return qtyCashSelectedItem;
    }
    updateIsCashAvailable() {
        var _a;
        const { instrument, account, productType, showLabel } = this.get();
        const orderTypeId = (_a = this.get('orderType')) === null || _a === void 0 ? void 0 : _a.id();
        if (isNullOrUndefined(account) ||
            isNullOrUndefined(instrument) ||
            isNullOrUndefined(orderTypeId) ||
            isNullOrUndefined(productType)) {
            return;
        }
        const suitableTradingEntity = showLabel; // Cash trading is only available from the main OE and in trading ideas – i.e., OEQuantity that has a label
        const isCashAvailable = IsAllowed.IsCashTradingAllowed(account, instrument, orderTypeId, productType) && suitableTradingEntity;
        void this.set('isCashAvailable', isCashAvailable);
    }
    onIsCashAvailableChanged(isCashAvailable) {
        const qtyCashSelectedItem = this.updateCashSelection(isCashAvailable);
        if (this.get('isCashMode') && !isCashAvailable) {
            void this.set({ isCashMode: false }); // when changing the order type to OCO while in Cash
        }
        void this.set({
            qtyCashSelectedItem
        });
        this.handleCashMarginRequest();
    }
    onQtyCashSelectedItemChanged(newItem, oldItem) {
        var _a;
        if ((newItem === null || newItem === void 0 ? void 0 : newItem.value) === (oldItem === null || oldItem === void 0 ? void 0 : oldItem.value)) {
            return;
        }
        const qtyCashSelectedItem = this.updateCashSelection();
        const isCashMode = this.isCashContextItem(qtyCashSelectedItem);
        let value = null; // #122525: If some value is entered for Cash or Quantity it should be saved if mode has been changed. If the mode is selected again, the previously saved value should be displayed.
        if (isCashMode) {
            value = (_a = this.get('cash')) !== null && _a !== void 0 ? _a : 1; // #124063+
        }
        else {
            const qty = this.get('quantity');
            if ((qty === null || qty === void 0 ? void 0 : qty.value) != null) {
                value = qty.toNumber(this.get('instrument'));
            }
        }
        void this.set({
            isCashMode,
            qtyCashSelectedItem
        }).then(() => {
            if (value > 0) {
                void this.set({ value }).then(() => {
                    this.onValueChanged(value);
                });
            }
        });
    }
    isCashContextItem(item) {
        return (item === null || item === void 0 ? void 0 : item.value) === OEQuantityCashContextItems.Cash;
    }
    updateEstQuantity() {
        var _a;
        const { estQtyText, estQtyValue } = this.get();
        if (estQtyValue == null) {
            void this.set({
                estQtyFormatted: `${estQtyText !== null && estQtyText !== void 0 ? estQtyText : ''} ${(_a = Resources.getResource('general.Calculating')) !== null && _a !== void 0 ? _a : ''}`
            });
            return;
        }
        const account = this.get('account');
        const instrument = this.get('instrument');
        const productType = this.get('productType');
        const inLots = this.get('inLots'); // #122525: `Display qty in lots setting` should be considered for Est. Quantity label
        const estQtyNumber = Quantity.convertQuantityValue(new Quantity(estQtyValue, true), instrument, inLots, account, productType);
        const estQtyValueFormatted = inLots
            ? InstrumentUtils.formatAmountValue(estQtyNumber, instrument, account, productType)
            : estQtyNumber;
        const estQtyFormatted = `${estQtyText !== null && estQtyText !== void 0 ? estQtyText : ''} ${estQtyValueFormatted !== null && estQtyValueFormatted !== void 0 ? estQtyValueFormatted : ''}`;
        void this.set({
            estQtyFormatted
        });
    }
    updateAssetName() {
        const account = this.get('account');
        const isCashMode = this.get('isCashMode');
        if (account == null) {
            return;
        }
        const assetNameForCash = isCashMode ? account.assetName : '';
        void this.set({
            assetNameForCash
        });
    }
    onIsCashModeChanged() {
        // for outside changing (e.g. callBack of OE)
        let { qtyCashSelectedItem, qtyCashItems, isCashMode } = this.get();
        SessionSettings.updateIsCashModePreferred(isCashMode);
        if (isCashMode === this.isCashContextItem(qtyCashSelectedItem)) {
            return;
        }
        qtyCashItems.forEach((item) => {
            if (isCashMode === this.isCashContextItem(item)) {
                qtyCashSelectedItem = item;
            }
        });
        void this.set({
            qtyCashSelectedItem
        });
    }
    prepareMarginRequestParameters() {
        var _a;
        if (this.get('skipMarginRequest') || !this.get('isCashMode')) {
            return null;
        }
        const parameters = new MarginInfoParameters();
        parameters.account = this.get('account');
        parameters.instrument = this.get('instrument');
        parameters.operation = this.get('operation');
        parameters.isCashMode = true;
        parameters.cash = this.get('cash');
        parameters.orderType = (_a = this.get('orderType')) === null || _a === void 0 ? void 0 : _a.id();
        parameters.validity = this.get('tif');
        parameters.productType = this.get('productType');
        const limitPrice = OrderEditBaseUtils.GetLimitPrice(this);
        if (limitPrice != null) {
            parameters.price = limitPrice;
        }
        const stopPrice = OrderEditBaseUtils.GetStopPrice(this);
        if (stopPrice != null) {
            parameters.stopPrice = stopPrice;
        }
        return parameters;
    }
    onPriceCouldChanged(orderEditParameterArray) {
        if (!this.get('isCashMode')) {
            return;
        }
        if (this.isTrailingStopOrder()) {
            const trailingStopOffsetValue = OrderEditBaseUtils.GetTrailingStopVisualOffset(this);
            if (this.lastTrailingStopOffsetValue !== trailingStopOffsetValue) {
                this.handleCashMarginRequest();
            }
            this.lastTrailingStopOffsetValue = trailingStopOffsetValue;
        }
        else {
            const newLimitPrice = OrderEditBaseUtils.GetLimitPrice(this);
            const newStopPrice = OrderEditBaseUtils.GetStopPrice(this);
            if (this.lastLimitPrice !== newLimitPrice || this.lastStopPrice !== newStopPrice) {
                this.handleCashMarginRequest();
            }
            this.lastLimitPrice = newLimitPrice;
            this.lastStopPrice = newStopPrice;
        }
    }
    handleCashMarginRequest() {
        if (this.cashMarginRequestTimeoutID != null) {
            clearTimeout(this.cashMarginRequestTimeoutID);
        }
        this.cashMarginRequestTimeoutID = setTimeout(() => {
            void (() => __awaiter(this, void 0, void 0, function* () {
                this.cashMarginRequestTimeoutID = null;
                if (!this.get('isCashMode')) {
                    return;
                }
                const parameters = this.prepareMarginRequestParameters();
                if ((parameters === null || parameters === void 0 ? void 0 : parameters.cash) == null) {
                    return;
                }
                void this.set('estQtyValue', undefined);
                const marginInfoWrapper = new MarginInfoWrapper();
                const amount = yield marginInfoWrapper.GetMarginAmount(parameters);
                if (isValidNumber(amount)) {
                    void this.set('estQtyValue', amount);
                }
            }))();
        }, this.cashMarginRequestTimeoutDuration);
    }
    observeIfMarginRequestNeeded() {
        if (this.autoMarginRequestIntervalID) {
            clearInterval(this.autoMarginRequestIntervalID);
            this.autoMarginRequestIntervalID = null;
        }
        const isMarketOrTrailingStop = this.isMarketOrTrailingStopOrder();
        if (isMarketOrTrailingStop) {
            this.autoMarginRequestIntervalID = setInterval(() => {
                this.handleCashMarginRequest();
            }, this.autoMarginRequestIntervalDuration);
            this.handleCashMarginRequest();
        }
    }
    isMarketOrTrailingStopOrder() {
        const orderType = this.get('orderType');
        const orderTypeId = orderType === null || orderType === void 0 ? void 0 : orderType.id();
        return orderTypeId === OrderType.Market || orderTypeId === OrderType.TrailingStop;
    }
    isTrailingStopOrder() {
        const orderType = this.get('orderType');
        const orderTypeId = orderType === null || orderType === void 0 ? void 0 : orderType.id();
        return orderTypeId === OrderType.TrailingStop;
    }
}
ContainerControl.extendWith(OEQuantity, {
    template: OEQuantityTemplate,
    data: function () {
        return {
            label: '',
            instrument: null,
            account: null,
            operation: null,
            orderType: null,
            tif: null,
            orderEditParameterArray: null,
            value: null,
            inLots: false,
            quantity: null,
            // TODO. Ugly. Remove.
            defaultQuantity: null,
            isCashAvailable: false,
            isCashMode: false,
            qtyCashItems: [
                // #122525
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.newOrderEntry.amountLabel',
                    value: OEQuantityCashContextItems.Quantity
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.newOrderEntry.cashLabel',
                    value: OEQuantityCashContextItems.Cash
                }
            ],
            qtyCashSelectedItem: null,
            qtyCashTooltip: 'Quantity or Cash can be selected.',
            skipMarginRequest: false,
            estQtyText: null,
            estQtyValue: null,
            estQtyFormatted: '', // = estQtyText + estQtyValue
            assetNameForCash: '', // a.k.a rightAlignedText in TerceraNumeric
            showLabel: true,
            noArrowCB: false,
            positionSizingVisible: false,
            positionSizingChecked: false
        };
    }
});
export var OEQuantityCashContextItems;
(function (OEQuantityCashContextItems) {
    OEQuantityCashContextItems[OEQuantityCashContextItems["Quantity"] = 0] = "Quantity";
    OEQuantityCashContextItems[OEQuantityCashContextItems["Cash"] = 1] = "Cash";
})(OEQuantityCashContextItems || (OEQuantityCashContextItems = {}));
